import React from 'react';
import { ArrowLeft, Brain, Zap, Target, BarChart3, CheckCircle, Star, ArrowRight, Bot, Eye, MessageSquare, TrendingUp, Database, Cpu, Network } from 'lucide-react';

interface AIPageProps {
  onBack: () => void;
}

const AIPage: React.FC<AIPageProps> = ({ onBack }) => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: Brain,
      title: "Machine Learning Avanzado",
      description: "Algoritmos de aprendizaje automático que mejoran continuamente con tus datos empresariales."
    },
    {
      icon: Eye,
      title: "Visión Artificial",
      description: "Procesamiento y análisis de imágenes para control de calidad, seguridad y automatización."
    },
    {
      icon: MessageSquare,
      title: "Procesamiento de Lenguaje Natural",
      description: "Chatbots inteligentes, análisis de sentimientos y procesamiento automático de documentos."
    },
    {
      icon: TrendingUp,
      title: "Análisis Predictivo",
      description: "Predicciones precisas para optimizar inventarios, ventas y operaciones empresariales."
    }
  ];

  const aiServices = [
    {
      name: "ChatGPT Integration",
      description: "Integración completa con OpenAI GPT-4",
      logo: "🤖"
    },
    {
      name: "Computer Vision",
      description: "Reconocimiento de imágenes y objetos",
      logo: "👁️"
    },
    {
      name: "Predictive Analytics",
      description: "Análisis predictivo con TensorFlow",
      logo: "📊"
    },
    {
      name: "Natural Language Processing",
      description: "Procesamiento de texto y voz",
      logo: "💬"
    },
    {
      name: "Recommendation Systems",
      description: "Sistemas de recomendación personalizados",
      logo: "🎯"
    },
    {
      name: "Automated Decision Making",
      description: "Toma de decisiones automatizada",
      logo: "⚡"
    }
  ];

  const useCases = [
    {
      title: "Chatbot Inteligente para Atención al Cliente",
      category: "Customer Service",
      description: "Sistema de atención automatizada que resuelve el 80% de consultas sin intervención humana.",
      features: ["Respuestas contextuales", "Escalación inteligente", "Análisis de sentimientos", "Integración CRM"],
      image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=600",
      results: "80% reducción en tiempo de respuesta"
    },
    {
      title: "Sistema de Predicción de Demanda",
      category: "Supply Chain",
      description: "IA que predice la demanda de productos con 95% de precisión para optimizar inventarios.",
      features: ["Análisis histórico", "Factores externos", "Optimización automática", "Alertas tempranas"],
      image: "https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=600",
      results: "30% reducción en costos de inventario"
    }
  ];

  const technologies = [
    {
      category: "Frameworks de IA",
      tools: ["TensorFlow", "PyTorch", "Scikit-learn", "Keras", "OpenCV", "Hugging Face"]
    },
    {
      category: "APIs de IA",
      tools: ["OpenAI GPT-4", "Google Cloud AI", "AWS SageMaker", "Azure Cognitive Services", "IBM Watson", "Anthropic Claude"]
    },
    {
      category: "Procesamiento de Datos",
      tools: ["Pandas", "NumPy", "Apache Spark", "Elasticsearch", "MongoDB", "PostgreSQL"]
    },
    {
      category: "Deployment & MLOps",
      tools: ["Docker", "Kubernetes", "MLflow", "Kubeflow", "Apache Airflow", "GitHub Actions"]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Análisis de Datos y Objetivos",
      description: "Evaluamos tus datos existentes y definimos objetivos específicos y métricas de éxito para el proyecto de IA."
    },
    {
      step: "02",
      title: "Preparación y Limpieza de Datos",
      description: "Procesamos y preparamos los datos para entrenar modelos de IA efectivos y precisos."
    },
    {
      step: "03",
      title: "Desarrollo y Entrenamiento",
      description: "Creamos y entrenamos modelos de IA personalizados usando las mejores técnicas y algoritmos."
    },
    {
      step: "04",
      title: "Validación y Testing",
      description: "Probamos exhaustivamente los modelos con datos reales para garantizar precisión y confiabilidad."
    },
    {
      step: "05",
      title: "Implementación y Monitoreo",
      description: "Desplegamos la solución de IA y monitoreamos continuamente su rendimiento y mejoras."
    }
  ];

  const benefits = [
    { metric: "95%", description: "Precisión en predicciones" },
    { metric: "70%", description: "Reducción en costos operativos" },
    { metric: "24/7", description: "Operación continua automatizada" },
    { metric: "10x", description: "Velocidad de procesamiento" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Volver a Servicios</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <img
                  src="/src/assets/logoModelos2.png"
                  alt="ModeloSmart Logo"
                  style={{ height: "120px", width: "auto" }}
                />
              </div>{" "}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                    <Brain className="h-8 w-8 text-green-600" />
                  </div>
                  <h1 className="text-5xl font-bold text-gray-900">
                    Inteligencia <span className="text-green-600">Artificial</span>
                  </h1>
                </div>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Transformamos tu negocio con soluciones de IA personalizadas que automatizan 
                  procesos, predicen tendencias y toman decisiones inteligentes en tiempo real.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="inline-flex items-center px-8 py-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Implementar IA
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                <button
                  onClick={() => scrollToSection('casos')}
                  className="inline-flex items-center px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-green-600 hover:text-green-600 transition-all duration-200"
                >
                  Ver Casos de Uso
                </button>
              </div>

              {/* Benefits Stats */}
              <div className="grid grid-cols-2 gap-6 pt-4">
                {benefits.slice(0, 2).map((benefit, index) => (
                  <div key={index} className="text-center p-4 bg-green-50 rounded-xl">
                    <div className="text-2xl font-bold text-green-600 mb-1">{benefit.metric}</div>
                    <div className="text-sm text-gray-600">{benefit.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Inteligencia Artificial y Machine Learning"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-green-600/20 to-transparent rounded-2xl"></div>
              
              {/* Floating elements */}
              <div className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center space-x-2">
                  <Bot className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">IA procesando</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Capacidades de <span className="text-green-600">Inteligencia Artificial</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Implementamos las tecnologías de IA más avanzadas para resolver desafíos 
              empresariales complejos y generar ventajas competitivas sostenibles.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Additional Benefits */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl border border-green-100">
                <div className="text-3xl font-bold text-green-600 mb-2">{benefit.metric}</div>
                <div className="text-gray-700 font-medium">{benefit.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Servicios de <span className="text-green-600">IA Disponibles</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ofrecemos una amplia gama de soluciones de inteligencia artificial 
              adaptadas a las necesidades específicas de tu industria.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiServices.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="text-4xl mb-4">{service.logo}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.name}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="casos" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Casos de <span className="text-green-600">Éxito</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ejemplos reales de cómo hemos implementado soluciones de IA que han 
              transformado operaciones empresariales y generado resultados medibles.
            </p>
          </div>

          <div className="space-y-16">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div>
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-600 text-sm font-medium rounded-full mb-4">
                      {useCase.category}
                    </span>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
                    <p className="text-lg text-gray-600 mb-6">{useCase.description}</p>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Características principales:</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {useCase.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-800">Resultado:</span>
                    </div>
                    <p className="text-green-700 mt-1">{useCase.results}</p>
                  </div>
                </div>

                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-80 object-cover rounded-2xl shadow-xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-green-600/10 to-transparent rounded-2xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Stack Tecnológico de <span className="text-green-600">IA</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Utilizamos las herramientas y frameworks más avanzados del ecosistema 
              de inteligencia artificial y machine learning.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">{tech.category}</h3>
                <div className="space-y-2">
                  {tech.tools.map((tool, idx) => (
                    <span
                      key={idx}
                      className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm mr-2 mb-2"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestro <span className="text-green-600">Proceso</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Metodología especializada en desarrollo de IA que garantiza resultados 
              medibles y soluciones que realmente impactan tu negocio.
            </p>
          </div>

          <div className="relative">
            {/* Process line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-600 to-blue-600 hidden lg:block"></div>
            
            <div className="space-y-12">
              {process.map((step, index) => (
                <div key={index} className="relative flex items-start">
                  <div className="hidden lg:flex w-16 h-16 bg-green-600 rounded-full items-center justify-center text-white font-bold text-lg mr-8 flex-shrink-0 shadow-lg">
                    {step.step}
                  </div>
                  
                  <div className="bg-gray-50 p-8 rounded-2xl flex-1 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                    <p className="text-gray-600 text-lg">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contacto" className="py-20 bg-gradient-to-br from-green-900 via-green-800 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              ¿Listo para <span className="text-yellow-400">potenciar tu negocio con IA</span>?
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              Descubre cómo la inteligencia artificial puede transformar tus procesos, 
              reducir costos y generar nuevas oportunidades de crecimiento.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 bg-white text-green-900 font-bold text-lg rounded-xl hover:bg-gray-100 transition-all duration-200 transform hover:scale-105 shadow-2xl"
              >
                Consulta Gratuita de IA
                <ArrowRight className="ml-3 h-6 w-6" />
              </button>
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 border-2 border-white/50 text-white font-semibold text-lg rounded-xl hover:bg-white/10 hover:border-white transition-all duration-200"
              >
                Ver Demo en Vivo
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIPage;